import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Puzzle, Copy, RefreshCw, Smartphone, Monitor, Clock, CheckCircle, XCircle } from "lucide-react";

interface LinkedDevice {
    id: string;
    device_name: string;
    linked_at: string | null;
    last_sync: string | null;
    is_active: boolean;
}

const Extension = () => {
    const navigate = useNavigate();
    const { toast } = useToast();
    const [linkingCode, setLinkingCode] = useState<string>("");
    const [codeExpiry, setCodeExpiry] = useState<Date | null>(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [linkedDevices, setLinkedDevices] = useState<LinkedDevice[]>([]);
    const [user, setUser] = useState<any>(null);
    const [timeLeft, setTimeLeft] = useState<string>("");

    useEffect(() => {
        checkAuth();
    }, []);

    useEffect(() => {
        if (user) {
            fetchLinkedDevices();
        }
    }, [user]);

    const checkAuth = async () => {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
            navigate("/login");
            return;
        }
        setUser(session.user);
    };

    const fetchLinkedDevices = async () => {
        if (!user) return;

        const { data, error } = await supabase
            .from('extension_codes')
            .select('*')
            .eq('user_id', user.id)
            .eq('used', true)
            .order('used_at', { ascending: false });

        if (error) {
            console.error('Error fetching linked devices:', error);
            return;
        }

        if (data) {
            setLinkedDevices(data.map((d) => ({
                id: d.id,
                device_name: d.device_name || 'Browser Extension',
                linked_at: d.used_at || d.created_at,
                last_sync: d.used_at || d.created_at,
                is_active: true
            })));
        }
    };

    const generateLinkingCode = async () => {
        if (!user) return;
        setIsGenerating(true);

        try {
            // Generate a random 6-character alphanumeric code
            const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
            let code = '';
            for (let i = 0; i < 6; i++) {
                code += chars[Math.floor(Math.random() * chars.length)];
            }

            // Set expiry to 5 minutes from now
            const expiry = new Date(Date.now() + 5 * 60 * 1000);

            // Save to Supabase
            const { error } = await supabase
                .from('extension_codes')
                .insert({
                    code: code,
                    user_id: user.id,
                    user_email: user.email,
                    expires_at: expiry.toISOString(),
                    used: false
                });

            if (error) {
                console.error('Error saving code:', error);
                toast({
                    title: "Error",
                    description: "Failed to generate code. Please try again.",
                    variant: "destructive",
                });
                setIsGenerating(false);
                return;
            }

            toast({
                title: "Code Generated!",
                description: "Enter this code in your TAM Tools extension within 5 minutes.",
            });

            setLinkingCode(code);
            setCodeExpiry(expiry);

        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to generate code. Please try again.",
                variant: "destructive",
            });
        }

        setIsGenerating(false);
    };

    const copyCode = () => {
        navigator.clipboard.writeText(linkingCode);
        toast({
            title: "Copied!",
            description: "Linking code copied to clipboard.",
        });
    };

    const unlinkDevice = async (deviceId: string) => {
        const { error } = await supabase
            .from('extension_codes')
            .delete()
            .eq('id', deviceId);

        if (error) {
            toast({
                title: "Error",
                description: "Failed to unlink device. Please try again.",
                variant: "destructive",
            });
            return;
        }

        setLinkedDevices(prev => prev.filter(d => d.id !== deviceId));
        toast({
            title: "Device Unlinked",
            description: "The device has been removed.",
        });
    };

    // Timer effect
    useEffect(() => {
        if (!codeExpiry) return;

        const updateTimer = () => {
            const diff = codeExpiry.getTime() - Date.now();
            if (diff <= 0) {
                setLinkingCode("");
                setCodeExpiry(null);
                setTimeLeft("");
                return;
            }
            const mins = Math.floor(diff / 60000);
            const secs = Math.floor((diff % 60000) / 1000);
            setTimeLeft(`${mins}:${secs.toString().padStart(2, '0')}`);
        };

        updateTimer();
        const interval = setInterval(updateTimer, 1000);
        return () => clearInterval(interval);
    }, [codeExpiry]);

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Puzzle className="h-6 w-6 text-primary" />
                        TAM Tools Extension
                    </h1>
                    <p className="text-muted-foreground">
                        Link your browser extension to sync data across devices
                    </p>
                </div>
                <a
                    href="https://microsoftedge.microsoft.com/addons/detail/tam-tools/..."
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <Button variant="outline" className="gap-2">
                        <Monitor className="h-4 w-4" />
                        Get Extension
                    </Button>
                </a>
            </div>

            {/* Link Extension Card */}
            <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Smartphone className="h-5 w-5" />
                        Link Your Extension
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                        Generate a one-time code to link your TAM Tools browser extension.
                        The code expires in 5 minutes.
                    </p>

                    {linkingCode ? (
                        <div className="space-y-4">
                            <div className="flex items-center justify-center gap-2 p-6 bg-background/50 rounded-lg border-2 border-dashed border-primary/30">
                                <div className="flex gap-2">
                                    {linkingCode.split('').map((char, i) => (
                                        <div
                                            key={i}
                                            className="w-12 h-14 flex items-center justify-center text-2xl font-bold bg-primary/10 border-2 border-primary/30 rounded-lg"
                                        >
                                            {char}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <Clock className="h-4 w-4" />
                                    Expires in: <span className="font-mono text-primary">{timeLeft}</span>
                                </div>
                                <div className="flex gap-2">
                                    <Button variant="outline" size="sm" onClick={copyCode}>
                                        <Copy className="h-4 w-4 mr-1" />
                                        Copy
                                    </Button>
                                    <Button variant="outline" size="sm" onClick={generateLinkingCode}>
                                        <RefreshCw className="h-4 w-4 mr-1" />
                                        New Code
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <Button
                            onClick={generateLinkingCode}
                            disabled={isGenerating}
                            className="w-full gap-2"
                            size="lg"
                        >
                            {isGenerating ? (
                                <RefreshCw className="h-4 w-4 animate-spin" />
                            ) : (
                                <Puzzle className="h-4 w-4" />
                            )}
                            Generate Linking Code
                        </Button>
                    )}
                </CardContent>
            </Card>

            {/* Instructions */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-lg">How to Link</CardTitle>
                </CardHeader>
                <CardContent>
                    <ol className="space-y-3 text-sm">
                        <li className="flex gap-3">
                            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-bold">1</span>
                            <span>Install the TAM Tools extension from the Edge Add-ons store</span>
                        </li>
                        <li className="flex gap-3">
                            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-bold">2</span>
                            <span>Click the extension icon to open the popup</span>
                        </li>
                        <li className="flex gap-3">
                            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-bold">3</span>
                            <span>Enter the 6-digit code shown above</span>
                        </li>
                        <li className="flex gap-3">
                            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-bold">4</span>
                            <span>Your extension is now linked and syncing!</span>
                        </li>
                    </ol>
                </CardContent>
            </Card>

            {/* Linked Devices */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-lg flex items-center justify-between">
                        <span>Linked Devices</span>
                        <Badge variant="outline">{linkedDevices.length} devices</Badge>
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {linkedDevices.length > 0 ? (
                        <div className="space-y-3">
                            {linkedDevices.map((device) => (
                                <div
                                    key={device.id}
                                    className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                                >
                                    <div className="flex items-center gap-3">
                                        <Monitor className="h-5 w-5 text-muted-foreground" />
                                        <div>
                                            <p className="font-medium">{device.device_name}</p>
                                            <p className="text-xs text-muted-foreground">
                                                Linked {device.linked_at ? new Date(device.linked_at).toLocaleDateString() : 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        {device.is_active ? (
                                            <Badge variant="default" className="gap-1">
                                                <CheckCircle className="h-3 w-3" />
                                                Active
                                            </Badge>
                                        ) : (
                                            <Badge variant="secondary" className="gap-1">
                                                <XCircle className="h-3 w-3" />
                                                Inactive
                                            </Badge>
                                        )}
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => unlinkDevice(device.id)}
                                            className="text-destructive hover:text-destructive"
                                        >
                                            Unlink
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-8 text-muted-foreground">
                            <Puzzle className="h-12 w-12 mx-auto mb-3 opacity-30" />
                            <p>No devices linked yet</p>
                            <p className="text-xs">Generate a code above to link your extension</p>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Sync Status */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-lg">Sync Status</CardTitle>
                </CardHeader>
                <CardContent className="grid gap-4 sm:grid-cols-3">
                    <div className="p-4 bg-muted/30 rounded-lg text-center">
                        <p className="text-2xl font-bold text-primary">0</p>
                        <p className="text-xs text-muted-foreground">Temp Mails Synced</p>
                    </div>
                    <div className="p-4 bg-muted/30 rounded-lg text-center">
                        <p className="text-2xl font-bold text-primary">0</p>
                        <p className="text-xs text-muted-foreground">Passwords Synced</p>
                    </div>
                    <div className="p-4 bg-muted/30 rounded-lg text-center">
                        <p className="text-2xl font-bold text-green-500">Active</p>
                        <p className="text-xs text-muted-foreground">Sync Status</p>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default Extension;